import { AxiosInstance } from 'axios';

import { FormName } from '@topconagriculture/feed-types';

import { Feeding } from '../../../store/entities/feeding/feeding.types';
import { Group, GroupCreate, GroupNote } from '../../../store/entities/group/group.types';
import { APIManagerConfig } from '../apiManager';
import { PreviewBunkReaderScorePayload } from '../../../store/application/groupPreview/groupPreview.types';
import { BunkScoresPayload } from '../../../store/entities/settings/settings.types';

const GroupServiceFactory = ({ HTTPBase, config }: { HTTPBase: AxiosInstance; config: APIManagerConfig }) => {
  return {
    getGroupList: (includeDeleted = false, includePreviousData = false): Promise<Group[]> =>
      HTTPBase.get(`farm/${config.farmId}/groups`, {
        params: {
          includeDeleted,
          includePreviousData,
          includeFeedingIngredients: true,
          includeBunkReader: true,
        },
      }).then((res) => res.data),
    getGroupById: (groupId: number): Promise<Group> =>
      HTTPBase.get(`farm/${config.farmId}/groups/${groupId}`, { params: { includeFeedingIngredients: true } }).then(
        (res) => res.data,
      ),
    getBunkReaderGroupById: (groupId: string, daysBack?: number, endDate?: Date): Promise<Group> =>
      HTTPBase.get(`farm/${config.farmId}/groups/${groupId}/bunkreader`, {
        params: {
          daysBack,
          endDate,
        },
      }).then((res) => res.data),
    addGroup: (group: GroupCreate, formName: FormName): Promise<Group> =>
      HTTPBase.post(`farm/${config.farmId}/groups`, group, { headers: { formName } }).then((res) => res.data),
    simulateCreateGroup: (group: GroupCreate): Promise<Group> =>
      HTTPBase.post(`farm/${config.farmId}/groups`, group, { params: { nosave: true } }).then((res) => res.data),
    simulateEditGroup: (group: Group): Promise<Group> =>
      HTTPBase.patch(`farm/${config.farmId}/groups/${group.id}`, group, { params: { nosave: true } }).then(
        (res) => res.data,
      ),
    simulateEditGroupBunkScore: (groupId: string, payload: PreviewBunkReaderScorePayload): Promise<Group> =>
      HTTPBase.patch(`farm/${config.farmId}/groups/${groupId}`, payload, {
        params: { nosave: true },
      }).then((res) => res.data),
    addBunkScores: (groupId: string, payload: BunkScoresPayload): Promise<BunkScoresPayload> =>
      HTTPBase.post(`farm/${config.farmId}/groups/${groupId}/bunkscores`, payload, {
        headers: { formName: FormName.bunkreader },
      }).then((res) => res.data),
    getBunkReaderNotes: (groupId: string): Promise<void> =>
      HTTPBase.get(`farm/${config.farmId}/groups/${groupId}/bunkreadernotes`).then((res) => res.data),
    addBunkReaderNote: (groupId: string, payload: Partial<GroupNote>): Promise<GroupNote> =>
      HTTPBase.post(`farm/${config.farmId}/groups/${groupId}/bunkreadernotes`, payload, {
        headers: { formName: FormName.bunkreaderNotes },
      }).then((res) => res.data),
    editBunkReaderNote: (groupId: string, note: GroupNote): Promise<GroupNote> =>
      HTTPBase.patch(`farm/${config.farmId}/groups/${groupId}/bunkreadernotes/${note.id}`, note, {
        headers: { formName: FormName.bunkreaderNotes },
      }).then((res) => res.data),
    deleteBunkReaderNote: (groupId: string, noteId: string): Promise<void> =>
      HTTPBase.delete(`farm/${config.farmId}/groups/${groupId}/bunkreadernotes/${noteId}`).then((res) => res.data),
    patchGroup: (group: Partial<Group>, groupId: string, formName: FormName, noSave = false): Promise<Group> => {
      return HTTPBase.patch(`farm/${config.farmId}/groups/${groupId}`, group, {
        headers: { formName },
        params: {
          includeFeedingIngredients: true,
          nosave: noSave,
        },
      }).then((res) => res.data);
    },
    patchGroups: (groups: Partial<Group>[], noSave, formName: FormName): Promise<Group> =>
      HTTPBase.patch(`farm/${config.farmId}/groups`, groups, {
        headers: { formName },
        params: { nosave: noSave },
      }).then((res) => res.data),
    deleteGroup: (groupId: string, formName: FormName): Promise<Group> =>
      HTTPBase.delete(`farm/${config.farmId}/groups/${groupId}`, { headers: { formName } }).then((res) => res.data),
    deleteFeeding: (groupId: string, feedingId: string, formName: FormName): Promise<any> =>
      HTTPBase.delete(`farm/${config.farmId}/groups/${groupId}/feedings/${feedingId}`, { headers: { formName } }).then(
        (res) => res.data,
      ),
    createFeeding: (groupId: string, feeding: Partial<Feeding>, formName: FormName): Promise<Feeding> =>
      HTTPBase.post(`farm/${config.farmId}/groups/${groupId}/feedings`, feeding, { headers: { formName } }).then(
        (res) => res.data,
      ),
    updateFeeding: (groupId: string, feeding: Partial<Feeding>, formName: FormName): Promise<Feeding> =>
      HTTPBase.patch(`farm/${config.farmId}/groups/${groupId}/feedings/${feeding.id}`, feeding, {
        headers: { formName },
      }).then((res) => res.data),
  };
};

export default GroupServiceFactory;
